import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { showPastVisitsAtom } from '@/modules/annotator/atoms';
import { useGetCurrentImage } from '@/modules/annotator/multiFileViewer/findingViewer/imageBrowser/helpers';
import { compareImagesAtom } from '@/modules/annotator/multiFileViewer/imageToolbar/atoms';
import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';

export interface ToothOption {
  value: string;
  label: string;
  universalId: string;
}

export const useTeethOptions = (): ToothOption[] => {
  const { displayToothNumber } = useUniversalToFDI();
  const selectedImages = useGetCurrentImage();
  const activeImage = selectedImages?.currentImage;
  const compareImages = useAtomValue(compareImagesAtom);
  const showPastVisits = useAtomValue(showPastVisitsAtom);

  return useMemo(() => {
    const options: ToothOption[] = [];
    const allTeeth = new Set<string>();

    // Check if we're in compare mode
    const isCompareMode =
      !!compareImages?.leftImage &&
      !!compareImages?.rightImage &&
      !!showPastVisits;

    // Get teeth from left image (if in compare mode)
    if (isCompareMode) {
      compareImages?.leftImage?.models?.tooth_identification?.findings
        ?.filter(
          (finding) => finding.toothNumber && finding.toothNumber !== 'missing',
        )
        .forEach((finding) => {
          allTeeth.add(String(finding.toothNumber));
        });

      // Get teeth from right image (if in compare mode)
      compareImages?.rightImage?.models?.tooth_identification?.findings
        ?.filter(
          (finding) => finding.toothNumber && finding.toothNumber !== 'missing',
        )
        .forEach((finding) => {
          allTeeth.add(String(finding.toothNumber));
        });
    } else {
      // Get teeth from active image (if not in compare mode)
      activeImage?.models?.tooth_identification?.findings
        ?.filter(
          (finding) => finding.toothNumber && finding.toothNumber !== 'missing',
        )
        .forEach((finding) => {
          allTeeth.add(String(finding.toothNumber));
        });
    }

    // Convert to options
    Array.from(allTeeth).forEach((toothId) => {
      options.push({
        value: toothId,
        label: displayToothNumber(toothId),
        universalId: toothId,
      });
    });

    return options.sort((a, b) => {
      // Sort by numeric value for adult teeth, then alphabetically for pediatric
      const aNum = parseInt(a.value);
      const bNum = parseInt(b.value);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      if (!isNaN(aNum)) return -1; // Numbers first
      if (!isNaN(bNum)) return 1;
      return a.value.localeCompare(b.value); // Alphabetical for letters
    });
  }, [displayToothNumber, activeImage, compareImages, showPastVisits]);
};
